import React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Check, Copy, RefreshCcw, ThumbsDown, ThumbsUp } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { tokens } from "@/theme/tokens";
import { Bolt, type BoltState } from "@/components/brand/Bolt";
import { MarkdownView } from "../Markdown/MarkdownView";
import { ArtifactCard } from "../Artifacts/ArtifactCard";
import { MediaCard } from "../Media/MediaCard";
import { KbImageCard } from "../Media/KbImageCard";
import { SubagentCard } from "../Subagent/SubagentCard";
import { WorkTimeline, deriveEntries } from "../WorkTimeline";
import { useEmbedUi } from "@/embed/EmbedUiContext";
import {
  ThoughtBubble,
  moodFromWorkEntries,
  type ThoughtBubbleMood,
} from "../ThoughtBubble";
import { entryVariants, resolveTransition } from "../motion";
import { TurnMetricsRow, hasMetrics } from "../TurnMetricsFooter";
import type { TurnMetrics } from "../../../chat/hooks/useStreamingTurn";
import { useSmoothedText } from "../../../chat/hooks/useSmoothedText";
import { mediaCitations, mediaKey } from "../../../chat/kbImageCitations";
import type { AssistantPart, Citation, FeedbackVote, WorkEntry } from "../../../chat/types";

/** Flattens assistant parts to plain text for the copy action — joins text
 *  parts, dropping non-text (media/artifact/subagent) which aren't copyable. */
function partsToPlainText(parts: AssistantPart[]): string {
  return parts
    .filter((p) => p.type === "text")
    .map((p) => (p as Extract<AssistantPart, { type: "text" }>).text)
    .join("\n\n")
    .trim();
}

/** Renders one assistant text part, optionally smoothing it into a
 *  typewriter-style progressive reveal (`smooth`). Extracted to its own
 *  component because `useSmoothedText` is a hook — it can't be called
 *  conditionally per-item inside the parts `.map()` below. Only the live
 *  turn's LAST text part passes `smooth: true`; every other part (and all of
 *  history) renders `text` instantly via `smooth: false` (the hook snaps to
 *  full immediately in that case). */
function AssistantTextPart({
  text,
  smooth,
  userQuestion,
}: {
  text: string;
  smooth: boolean;
  userQuestion?: string;
}) {
  const display = useSmoothedText(text, smooth);
  // `smooth` is true only for the live turn's still-streaming last text part —
  // the same gate that drives the soft per-word fade in MarkdownView. Settled
  // parts / history render plain (no fade spans).
  return <MarkdownView source={display} streaming={smooth} userQuestion={userQuestion} />;
}

export interface AssistantBubbleProps {
  parts: AssistantPart[] | string;
  onRegenerate?: () => void | Promise<void>;
  /** Persisted extended-reasoning text from the completed turn. Degraded-mode
   *  fallback for WorkTimeline (used to derive a timeline when `workLog` is
   *  absent, e.g. old messages). Only passed when the `thinking` feature flag
   *  is on (MessageList gates it). */
  thinking?: string;
  /** True while reasoning is still streaming (live turn) — drives
   *  WorkTimeline's expanded/shimmer "Working…" state. */
  thinkingStreaming?: boolean;
  /** Measured reasoning duration (ms) → WorkTimeline's "Thought for Xs". */
  thinkingMs?: number;
  /** Bolt avatar expression. "thinking" while the model is reasoning. */
  avatarState?: BoltState;
  /** When true, the avatar's eyes blink on a ~5s cadence. Set for the most
   *  recent assistant message so the resting avatar feels alive. */
  blink?: boolean;
  /** True for the in-flight live turn — makes the WorkingPill read "Working…". */
  isLive?: boolean;
  /** Persisted tool-call records from the completed turn. Degraded-mode
   *  fallback for WorkTimeline when `workLog` is absent. */
  toolCalls?: { name: string; label?: string; isError?: boolean }[];
  /** Total turn latency (ms). Currently unused by WorkTimeline (which uses
   *  `thinkingMs` for its collapsed-header duration); kept for callers that
   *  still pass it. */
  durationMs?: number;
  /** Narration lines promoted during the turn. Degraded-mode fallback for
   *  WorkTimeline when `workLog` is absent. */
  workingNotes?: string[];
  /** Citations accumulated during the turn. Degraded-mode fallback for
   *  WorkTimeline (attached to the last tool step) when `workLog` is absent —
   *  sources render inside the timeline's tool entries, not as a standalone
   *  panel. */
  citations?: Citation[];
  /** Chronological work timeline (reasoning + narration + tool steps) for this
   *  turn. Drives the single WorkTimeline disclosure above the answer. When
   *  absent (old messages), WorkTimeline derives a degraded timeline from the
   *  thinking/toolCalls/workingNotes/citations props above. */
  workLog?: WorkEntry[];
  /** Current persisted feedback vote for this message (drives the thumbs
   *  toggle state). Only set for persisted history messages. */
  feedback?: FeedbackVote | null;
  /** Submit a feedback vote. Toggling the active vote off passes null. When
   *  absent the thumbs row is hidden (e.g. the live in-flight bubble). */
  onFeedback?: (vote: FeedbackVote | null) => void | Promise<void>;
  /** Turn metrics (cost/latency/tokens/model). When present they render inline
   *  on the same footer row as the copy/feedback/regenerate actions. */
  metrics?: TurnMetrics | null;
  /** Forward to WorkTimeline: start collapsed even while streaming. */
  defaultCollapsed?: boolean;
  /** The user turn this reply answers, forwarded to MarkdownView so a table
   *  export can name the question it came from. A scalar on purpose — the
   *  bubble is memoized and an object would re-render every history bubble on
   *  every streamed token. Absent for replies with no preceding user turn. */
  userQuestion?: string;
}

/** Export-only cards are downloads attached to the reply, so they go after it.
 *  Live, a tool can create them before its answer text streams (audit_invoice
 *  does), which put the cards above the reply; history persists them after the
 *  text. Moving them last keeps the two the same. Stable for everything else. */
function exportOnlyLast(parts: AssistantPart[]): AssistantPart[] {
  const isExportOnly = (p: AssistantPart) =>
    p.type === "artifact" && p.display === "export_only";
  if (!parts.some(isExportOnly)) return parts;
  return [...parts.filter((p) => !isExportOnly(p)), ...parts.filter(isExportOnly)];
}

/**
 * Renders an assistant message with its content, work timeline, avatar, actions, and metrics.
 *
 * @param parts - The assistant response content and any associated media, subagent, or artifact parts.
 * @param defaultCollapsed - Whether the work timeline starts collapsed.
 * @returns The rendered assistant message.
 */
function AssistantBubbleImpl({ parts, onRegenerate, thinking, thinkingStreaming, thinkingMs, avatarState = "idle", blink, isLive, toolCalls, durationMs, workingNotes, citations, workLog, feedback, onFeedback, metrics, defaultCollapsed, userQuestion }: AssistantBubbleProps) {
  const { compactDesign } = useEmbedUi();
  const normalized: AssistantPart[] = exportOnlyLast(
    typeof parts === "string" ? [{ type: "text", text: parts }] : parts,
  );
  // Index of the last text part — only it is still "live" (streaming) on the
  // in-flight turn; any text part before it (and all of history) is final.
  const lastTextIndex = normalized.reduce(
    (last, p, idx) => (p.type === "text" ? idx : last),
    -1,
  );

  // Clicking Bolt gives a brief "happy" reaction, then reverts to whatever the
  // turn's expression is (idle, or thinking/talking on a live turn).
  const [happy, setHappy] = React.useState(false);
  const happyTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const onAvatarClick = () => {
    setHappy(true);
    if (happyTimer.current) clearTimeout(happyTimer.current);
    happyTimer.current = setTimeout(() => setHappy(false), 1500);
  };
  React.useEffect(() => () => { if (happyTimer.current) clearTimeout(happyTimer.current); }, []);

  const reduce = useReducedMotion() ?? false;
  const theme = useTheme();

  const [copied, setCopied] = React.useState(false);
  const plainText = partsToPlainText(normalized);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked (insecure context / permission) — no-op */
    }
  };

  // The action row is shown whenever there's anything to act on: copyable
  // text, a regenerate handler, or a feedback handler. (Previously it only
  // appeared for the regenerate-able last message.)
  const hasActions = (!!plainText && plainText.length > 0) || !!onRegenerate || !!onFeedback;
  const showMetrics = hasMetrics(metrics ?? null);

  // Shared quiet style for the hover action buttons (copy / thumbs / regen).
  const actionBtnSx = {
    color: "text.secondary",
    "&:hover": { bgcolor: (t: typeof theme) => alpha(t.palette.text.primary, 0.1), color: "text.primary" },
  } as const;

  const timelineStreaming = !!(thinkingStreaming || isLive);
  const timelineEntries = deriveEntries({
    entries: workLog,
    thinking,
    toolCalls,
    workingNotes,
    citations,
  });
  // Cited knowledge images, shown under the answer (spec §12). Recomputed
  // from props, so a reloaded history re-derives (and re-fetches) them.
  const imageCitations = React.useMemo(() => mediaCitations(citations, workLog), [citations, workLog]);
  const thoughtMood: ThoughtBubbleMood = (() => {
    if (happy) return "happy";
    // Answer streaming: calm avatar; work status lives in the timeline copy.
    if (avatarState === "talking") return "done";
    if (timelineStreaming || avatarState === "thinking") {
      return moodFromWorkEntries(timelineEntries, true);
    }
    return "done";
  })();

  return (
    <motion.div
      initial={reduce ? false : "hidden"}
      animate="visible"
      variants={entryVariants}
      transition={resolveTransition(reduce)}
      // Small top margin keeps the answer attached to its user prompt; the
      // larger bottom margin opens space before the next exchange. (Paired
      // with UserBubble's asymmetric margins to group each turn.)
      style={{ display: "flex", gap: 12, marginTop: 6, marginBottom: 14, alignItems: "flex-start" }}
    >
      {/* Avatar: compact embed uses the animated thought bubble; SPA/classic
          embed keeps Bolt. The wrapper carries the "Artie" accessible name. */}
      <Box
        component="button"
        type="button"
        data-ph="avatar-refresh_spin"
        onClick={onAvatarClick}
        aria-label="Artie"
        sx={{
          mt: 0.25,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          p: 0,
          border: 0,
          background: "transparent",
          cursor: "pointer",
        }}
      >
        {compactDesign ? (
          <ThoughtBubble mood={thoughtMood} size={36} />
        ) : (
          <Bolt size={34} state={happy ? "happy" : avatarState} blink={blink} decorative />
        )}
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          // No bubble for the assistant — the reply renders flat on the page
          // (only user messages get a bubble). A small top padding keeps the
          // first line aligned with the avatar.
          pt: 0.5,
        }}
      >
        <WorkTimeline
          entries={workLog}
          streaming={thinkingStreaming || isLive}
          durationMs={thinkingMs}
          thinking={thinking}
          toolCalls={toolCalls}
          workingNotes={workingNotes}
          citations={citations}
          defaultCollapsed={defaultCollapsed}
        />
        {normalized.map((p, i) => {
          if (p.type === "text") {
            return (
              <AssistantTextPart
                key={i}
                text={p.text}
                smooth={Boolean(isLive) && i === lastTextIndex}
                userQuestion={userQuestion}
              />
            );
          }
          if (p.type === "media") return <MediaCard key={i} part={p} />;
          if (p.type === "subagent") return <SubagentCard key={`subagent-${p.job_id}`} part={p} />;
          return <ArtifactCard key={i} part={p} />;
        })}
        {imageCitations.length > 0 && (
          <Box data-testid="kb-image-cards" sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
            {imageCitations.map((c) => (
              <KbImageCard key={mediaKey(c.media!)} citation={c} />
            ))}
          </Box>
        )}
        {(hasActions || showMetrics) && (
          <Box
            sx={{
              mt: 0.75,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            {/* Action buttons — always visible. */}
            {hasActions && (
              <Box
                className="AssistantBubble-actions"
                data-ph-scope="message"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.25,
                }}
              >
                {plainText && (
                  <Tooltip title={copied ? "Copied" : "Copy"}>
                    <IconButton size="small" data-ph="copy" onClick={onCopy} aria-label="copy message" sx={actionBtnSx}>
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                    </IconButton>
                  </Tooltip>
                )}
                {onFeedback && (
                  <>
                    <Tooltip title="Good response">
                      <IconButton
                        size="small"
                        data-ph="rate_good"
                        onClick={() => onFeedback(feedback === "up" ? null : "up")}
                        aria-label="good response"
                        aria-pressed={feedback === "up"}
                        sx={{ ...actionBtnSx, color: feedback === "up" ? "primary.main" : undefined }}
                      >
                        <ThumbsUp size={14} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Bad response">
                      <IconButton
                        size="small"
                        data-ph="rate_bad"
                        onClick={() => onFeedback(feedback === "down" ? null : "down")}
                        aria-label="bad response"
                        aria-pressed={feedback === "down"}
                        sx={{ ...actionBtnSx, color: feedback === "down" ? "error.main" : undefined }}
                      >
                        <ThumbsDown size={14} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                {onRegenerate && (
                  <Tooltip title="Regenerate response">
                    <IconButton size="small" data-ph="regenerate" onClick={onRegenerate} aria-label="regenerate response" sx={actionBtnSx}>
                      <RefreshCcw size={14} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
            {/* Metrics — always visible, sitting next to the actions. */}
            {showMetrics && <TurnMetricsRow metrics={metrics ?? null} />}
          </Box>
        )}
      </Box>
    </motion.div>
  );
}

// Memoized so completed history bubbles don't re-render on every streamed
// token. Default shallow compare is right: a completed message's props
// (parts array, citations, etc.) keep stable identity across turns, while the
// live streaming bubble passes a fresh `parts` array each token and re-renders
// as intended.
export const AssistantBubble = React.memo(AssistantBubbleImpl);
